import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Helmet } from 'react-helmet-async';
import { useUser } from '../contexts/UserContext';
import { createSubscription, getSubscriptionStatus, getPricingPage } from '../services/api';
import Tips from '../components/dialogs/Tips';

type AnnouncementVariant = 'primary' | 'info' | 'warning' | 'success';
interface Announcement {
  enabled: boolean;
  text: string;
  variant?: AnnouncementVariant;
}

const ANNOUNCEMENT_STYLES: Record<AnnouncementVariant, { border: string; bg: string; iconColor: string; Icon: React.ElementType }> = {
  primary: { border: 'rgba(217,255,0,0.4)', bg: 'rgba(217,255,0,0.04)', iconColor: 'primary.main', Icon: AutoAwesomeIcon },
  info:    { border: 'rgba(100,181,246,0.45)', bg: 'rgba(100,181,246,0.08)', iconColor: '#64b5f6', Icon: InfoOutlinedIcon },
  warning: { border: 'rgba(255,183,77,0.45)', bg: 'rgba(255,183,77,0.08)', iconColor: '#ffb74d', Icon: WarningAmberIcon },
  success: { border: 'rgba(129,199,132,0.45)', bg: 'rgba(129,199,132,0.08)', iconColor: '#81c784', Icon: CheckCircleOutlineIcon },
};

interface Feature {
  text: string;
  coming_soon?: boolean;
  ea_now?: boolean;
  tips?: Feature[];
  tips_description?: Feature;
}

interface Plan {
  name: string;
  plan_type: number;
  price_monthly: number;
  price_yearly: number;
  description: string;
  features: (string | Feature)[];
  highlight?: boolean;
  cta: string;
}

const toFeatureObj = (f: string | Feature): Feature =>
  typeof f === 'string' ? { text: f } : f;

// 特典マーカーの判定
// [SUP1]/[SUP2]: 開発支援（アウトラインサムズアップ）
// [EA]: プレミアム項目（グラデーション + スパークル）
type FeatureKind = 'normal' | 'sup1' | 'sup2' | 'ea';
const parseFeature = (text: string): { kind: FeatureKind; text: string } => {
  if (text.startsWith('[SUP2]')) return { kind: 'sup2', text: text.slice(6) };
  if (text.startsWith('[SUP1]')) return { kind: 'sup1', text: text.slice(6) };
  if (text.startsWith('[EA]')) return { kind: 'ea', text: text.slice(4) };
  return { kind: 'normal', text };
};

const EA_GRADIENT = 'linear-gradient(90deg, #ffd700 0%, #ff6ec7 100%)';

// アイコン用のグラデーション定義（SVGのfillに適用）
const EaGradientDef = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }}>
    <defs>
      <linearGradient id="ea-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#ffd700" />
        <stop offset="100%" stopColor="#ff6ec7" />
      </linearGradient>
    </defs>
  </svg>
);

// **text** をアクセント表示に変換
const renderFeatureText = (text: string): React.ReactNode => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Box key={i} component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
          {part.slice(2, -2)}
        </Box>
      );
    }
    return part;
  });
};

// Tips内の説明文モード（リストの代わりに自由テキスト1ブロックを表示）
const renderTipsDescription = (raw: string | Feature): React.ReactNode => {
  const feat = toFeatureObj(raw);
  const { kind, text } = parseFeature(feat.text);
  const comingSoon = !!feat.coming_soon;
  const eaNow = !!feat.ea_now;
  const dimmed = comingSoon || eaNow;
  const textSx = dimmed
    ? { color: 'text.disabled' }
    : kind === 'ea'
    ? {
        background: EA_GRADIENT,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: 700,
      }
    : { color: 'text.primary' };
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="body2" sx={{ ...textSx, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
        {renderFeatureText(text)}
      </Typography>
      {(eaNow || comingSoon) && (
        <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {eaNow && (
            <Chip
              label="early"
              size="small"
              sx={{ height: 18, fontSize: '0.65rem', background: EA_GRADIENT, color: '#000', fontWeight: 400 }}
            />
          )}
          {comingSoon && (
            <Chip
              label="開発中"
              size="small"
              sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.08)', color: 'text.secondary' }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

// 機能行をレンダリング。allowTips=false の場合は ？ を出さない（Tips内のネスト用）。
const renderFeatureRow = (raw: string | Feature, key: React.Key, allowTips: boolean): React.ReactNode => {
  const feat = toFeatureObj(raw);
  const { kind, text } = parseFeature(feat.text);
  const comingSoon = !!feat.coming_soon;
  const eaNow = !!feat.ea_now;
  const dimmed = comingSoon || eaNow;
  const icon =
    kind === 'ea' ? (
      <AutoAwesomeIcon sx={{ fontSize: '1rem', fill: dimmed ? undefined : 'url(#ea-gradient)', color: dimmed ? 'text.disabled' : undefined }} />
    ) : kind === 'sup1' || kind === 'sup2' ? (
      <ThumbUpOutlinedIcon sx={{ fontSize: '1rem', color: dimmed ? 'text.disabled' : 'primary.main' }} />
    ) : (
      <CheckIcon sx={{ fontSize: '1rem', color: dimmed ? 'text.disabled' : 'primary.main' }} />
    );
  const textSx = dimmed
    ? { color: 'text.disabled' }
    : kind === 'ea'
    ? {
        background: EA_GRADIENT,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: 700,
      }
    : { color: 'text.primary' };
  const tipsDescription = allowTips && feat.tips_description && (feat.tips_description.text || '').length > 0 ? feat.tips_description : null;
  const tipsList = allowTips && !tipsDescription && feat.tips && feat.tips.length > 0 ? feat.tips : null;
  const hasTips = !!tipsDescription || !!tipsList;
  const tipsTitle = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  return (
    <ListItem key={key} disableGutters sx={{ py: 0.4, alignItems: 'flex-start' }}>
      <ListItemIcon sx={{ minWidth: 28, mt: '2px' }}>{icon}</ListItemIcon>
      <ListItemText
        primary={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
            <Box component="span">{renderFeatureText(text)}</Box>
            {eaNow && (
              <Chip
                label="early"
                size="small"
                sx={{ height: 16, fontSize: '0.6rem', background: EA_GRADIENT, color: '#000', fontWeight: 400 }}
              />
            )}
            {comingSoon && (
              <Chip
                label="開発中"
                size="small"
                sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(255,255,255,0.08)', color: 'text.secondary' }}
              />
            )}
            {hasTips && (
              <Tips
                title={tipsTitle}
                buttonComponent={
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      color: 'text.secondary',
                      lineHeight: 0,
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: '0.95rem' }} />
                  </Box>
                }
                content={
                  tipsDescription ? (
                    renderTipsDescription(tipsDescription)
                  ) : (
                    <List dense disablePadding>
                      {tipsList!.map((t, i) => renderFeatureRow(t, i, false))}
                    </List>
                  )
                }
              />
            )}
          </Box>
        }
        primaryTypographyProps={{ variant: 'body2', sx: textSx }}
      />
    </ListItem>
  );
};

const PricingPage: React.FC = () => {
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('yearly');
  const [loadingPlan, setLoadingPlan] = useState<number | null>(null);
  const [userPlanType, setUserPlanType] = useState<number>(0);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const { isAuthenticated } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    getPricingPage()
      .then(res => {
        setPlans(res.data.plans || []);
        // 新形式 announcements を優先、なければ旧形式 announcement を1件に変換
        const list: Announcement[] = Array.isArray(res.data.announcements) && res.data.announcements.length > 0
          ? res.data.announcements
          : res.data.announcement
          ? [{ enabled: !!res.data.announcement.enabled, text: res.data.announcement.text || '', variant: 'primary' }]
          : [];
        setAnnouncements(list);
      })
      .catch(() => setPlans([]));
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      getSubscriptionStatus()
        .then(res => {
          if (res.data.is_active || res.data.plan_type === 9) {
            setUserPlanType(res.data.plan_type);
          }
        })
        .catch(() => {});
    }
  }, [isAuthenticated]);

  const maxMonthsSaved = plans.reduce((max, p) => {
    if (p.price_monthly <= 0 || p.price_yearly <= 0) return max;
    return Math.max(max, Math.floor(12 - p.price_yearly / p.price_monthly));
  }, 0);

  const handleSubscribe = async (plan: Plan) => {
    if (plan.plan_type === 0) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // 既にサブスク登録者ならプラン管理ページへ
    if (userPlanType > 0) {
      navigate('/profile/settings?tab=subscription');
      return;
    }
    try {
      setLoadingPlan(plan.plan_type);
      const response = await createSubscription({
        plan_type: plan.plan_type,
        billing_period: billing,
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch {
      navigate('/profile/settings?tab=subscription');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>料金プラン - PULSE PRO</title>
      </Helmet>
      <EaGradientDef />
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
            PULSE PRO
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
            サポーターになり機能を開放することで、より快適にPULSEをお楽しみいただけます。
          </Typography>
          {userPlanType === 9 && (
            <Box
              mx="auto"
              mb={3}
              py={1.5}
              px={2.5}
              sx={{
                maxWidth: 520,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.main',
                bgcolor: 'rgba(217,255,0,0.05)',
              }}
            >
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                クラウドファンディング支援特典が適用されています
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                プラン機能を特典として提供中のため、新規加入は不要です。
              </Typography>
            </Box>
          )}
          {announcements
            .filter(a => a.enabled && a.text)
            .map((a, i) => {
              const style = ANNOUNCEMENT_STYLES[a.variant || 'primary'];
              const Icon = style.Icon;
              return (
                <Box
                  key={i}
                  mx="auto"
                  mb={1.25}
                  py={1.25}
                  px={2.5}
                  sx={{
                    maxWidth: 640,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: style.border,
                    bgcolor: style.bg,
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                  }}
                >
                  <Icon sx={{ fontSize: '1.1rem', color: style.iconColor, mt: '2px', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                    {a.text}
                  </Typography>
                </Box>
              );
            })}
          <ToggleButtonGroup
            value={billing}
            exclusive
            onChange={(_, v) => v && setBilling(v)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: { xs: 3, sm: 6 },
                py: 0.75,
                textTransform: 'none',
                fontSize: '0.85rem',
                border: '1px solid rgba(255,255,255,0.12)',
                '&.Mui-selected': {
                  bgcolor: 'transparent',
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': { bgcolor: 'rgba(217,255,0,0.08)' },
                },
              },
            }}
          >
            <ToggleButton value="yearly">
              年額
              <Chip
                label={maxMonthsSaved > 0 ? `最大${maxMonthsSaved}ヶ月分お得` : '年額がお得'}
                size="small"
                sx={{
                  ml: 0.75,
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  bgcolor: 'rgba(217,255,0,0.15)',
                  color: 'primary.main',
                }}
              />
            </ToggleButton>
            <ToggleButton value="monthly">月額</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Plan Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
            alignItems: 'stretch',
            pt: 3,
          }}
        >
          {plans.map((plan) => {
            const isYearly = billing === 'yearly';
            const price = isYearly ? plan.price_yearly : plan.price_monthly;
            const monthlyEquivalent = isYearly && plan.price_yearly > 0
              ? Math.round(plan.price_yearly / 12)
              : null;
            const isHighlight = plan.highlight;

            return (
              <Card
                key={plan.name}
                sx={{
                  position: 'relative',
                  border: isHighlight
                    ? '2px solid'
                    : '1px solid rgba(255,255,255,0.08)',
                  borderColor: isHighlight ? 'primary.main' : undefined,
                  borderRadius: 3,
                  bgcolor: isHighlight
                    ? 'rgba(217,255,0,0.03)'
                    : 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'visible',
                  ":hover": {
                    transform: 'none',
                  },
                }}
              >
                {isHighlight && (
                  <Chip
                    label="おすすめ"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -14,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: 'primary.main',
                      color: '#000',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      zIndex: 1,
                    }}
                  />
                )}
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                  }}
                >
                  {/* Plan Name */}
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 2,
                      color: isHighlight ? 'primary.main' : 'text.secondary',
                      mb: 0.5,
                    }}
                  >
                    {plan.name}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', mb: 2 }}
                  >
                    {plan.description}
                  </Typography>

                  {/* Price - fixed height so buttons align */}
                  <Box sx={{ mb: 3, minHeight: 80 }}>
                    {isYearly && plan.price_monthly > 0 && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.disabled',
                          textDecoration: 'line-through',
                          mb: 0.25,
                        }}
                      >
                        ¥{(plan.price_monthly * 12).toLocaleString()}/年
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        ¥{price.toLocaleString()}
                      </Typography>
                      {price > 0 && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          /{isYearly ? '年' : '月'}
                        </Typography>
                      )}
                      {isYearly && plan.price_monthly > 0 && plan.price_yearly > 0 && (
                        <Chip
                          label={`${Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}% OFF`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            bgcolor: 'rgba(217,255,0,0.15)',
                            color: 'primary.main',
                          }}
                        />
                      )}
                    </Box>
                    {monthlyEquivalent && (
                      <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        月あたり ¥{monthlyEquivalent.toLocaleString()}
                      </Typography>
                    )}
                    {price === 0 && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        ずっと無料
                      </Typography>
                    )}
                  </Box>

                  {/* CTA Button */}
                  <Button
                    variant={isHighlight && plan.plan_type !== userPlanType ? 'contained' : 'outlined'}
                    fullWidth
                    disabled={userPlanType === 9 || (isAuthenticated && plan.plan_type === 0) || (userPlanType > 0 && plan.plan_type === userPlanType) || loadingPlan !== null}
                    onClick={() => plan.plan_type === 0 ? navigate('/login') : handleSubscribe(plan)}
                    sx={{
                      mb: 3,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      ...(isHighlight
                        ? { bgcolor: 'primary.main', color: '#000' }
                        : {
                            borderColor: 'rgba(255,255,255,0.2)',
                            color: 'text.primary',
                          }),
                    }}
                  >
                    {loadingPlan === plan.plan_type ? (
                      <CircularProgress size={20} sx={{ color: 'inherit' }} />
                    ) : userPlanType === 9 && plan.plan_type > 0 ? (
                      '特典適用中'
                    ) : userPlanType > 0 && plan.plan_type === userPlanType ? (
                      '現在のプラン'
                    ) : (
                      plan.cta
                    )}
                  </Button>

                  {/* Features */}
                  <List dense disablePadding sx={{ flexGrow: 1 }}>
                    {plan.features.map((raw, i) => renderFeatureRow(raw, i, true))}
                  </List>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Stripe セキュリティバッジ */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={1}
          mt={5}
          py={1.5}
          px={2}
          mx="auto"
          sx={{
            maxWidth: 520,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'rgba(255,255,255,0.02)',
          }}
        >
          <Box
            component="svg"
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            sx={{ color: 'success.main', flexShrink: 0 }}
          >
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" fill="currentColor" opacity="0.2"/>
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M10 15.5l-3.5-3.5 1.41-1.41L10 12.67l5.59-5.59L17 8.5l-7 7z" fill="currentColor"/>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', letterSpacing: 0.3 }}>
            当サービスではカード情報を一切取得・保存しません。決済はPCI DSS準拠の<Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>Stripe</Box>を通じて安全に処理されます。
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default PricingPage;
